import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Plus, GraduationCap, AlertTriangle,
  TrendingUp, Activity, Calendar, ChevronRight, Search,
  UserPlus, Settings, BarChart3, Shield, Megaphone, Clock,
  UserCog, Trash2, Edit, Bell, X, FileText, Briefcase,
  Download, RefreshCw, CheckCircle, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../services/api';

const API_URL = getApiBaseUrl();

export default function PrepOSAdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [activeTab, setActiveTab] = useState('institutes');
  
  // Get current user info from localStorage
  const storedUser = localStorage.getItem('user');
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const isMainAdmin = currentUser?.email === 'hariravi28@gmail.com';  // Only main admin can create institutes
  
  // Data states
  const [institutes, setInstitutes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchMembers, setBatchMembers] = useState([]);
  const [atRiskStudents, setAtRiskStudents] = useState([]);
  
  // Phase 4B: Staff, Schedule, Announcements
  const [staffMembers, setStaffMembers] = useState([]);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  
  // Phase 4C: Operations data
  const [interventions, setInterventions] = useState([]);
  const [workboardStats, setWorkboardStats] = useState(null);
  const [workboardStudents, setWorkboardStudents] = useState([]);
  const [reports, setReports] = useState([]);
  
  // Form states
  const [showInstituteForm, setShowInstituteForm] = useState(false);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [showInterventionForm, setShowInterventionForm] = useState(false);
  
  // Form data
  const [instituteForm, setInstituteForm] = useState({
    name: '', code: '', city: '', state: '', contact_email: ''
  });
  const [batchForm, setBatchForm] = useState({
    name: '', code: '', description: '', target_exam: 'UPSC CSE', target_year: 2026, max_students: 50
  });
  const [memberEmail, setMemberEmail] = useState('');
  const [interventionForm, setInterventionForm] = useState({
    student_id: '', intervention_type: 'counseling', reason: '', description: '', priority: 'medium'
  });
  
  // Phase 4B form data
  const [staffForm, setStaffForm] = useState({
    user_id: '', role: 'faculty', assigned_batches: []
  });
  const [scheduleForm, setScheduleForm] = useState({
    title: '', description: '', event_type: 'class', start_date: '', end_date: '', start_time: '', end_time: ''
  });
  const [announcementForm, setAnnouncementForm] = useState({
    title: '', content: '', priority: 'medium', batch_id: '', target_roles: ['student']
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInstitutes();
  }, []);

  useEffect(() => {
    if (selectedInstitute) {
      fetchBatches(selectedInstitute.id);
      fetchStaffMembers(selectedInstitute.id);
      fetchAnnouncements(selectedInstitute.id);
      fetchInterventions(selectedInstitute.id);
    }
  }, [selectedInstitute]);

  useEffect(() => {
    if (selectedBatch) {
      fetchBatchMembers(selectedBatch.id);
      fetchAtRiskStudents(selectedBatch.id);
      fetchScheduleEvents(selectedBatch.id);
    }
  }, [selectedBatch]);

  // Phase 4C: Fetch operations data
  useEffect(() => {
    fetchWorkboardStats();
    fetchWorkboardStudents();
    fetchReports();
  }, []);

  const fetchInstitutes = async () => {
    try {
      // Admin sees all institutes via /list, Directors see only their institutes via /my-institutes
      const endpoint = isMainAdmin ? '/list' : '/my-institutes';
      const res = await fetch(`${API_URL}/api/institutes${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (isMainAdmin) {
        // Admin endpoint returns institutes directly
        setInstitutes(data);
      } else {
        // Director endpoint returns {institute, role, ...} objects
        const instituteList = data.map(item => ({
          ...item.institute,
          userRole: item.role,
          membershipType: item.membership_type
        }));
        setInstitutes(instituteList);
      }
    } catch (err) {
      console.error('Failed to fetch institutes:', err);
    }
  };

  const fetchBatches = async (instituteId) => {
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/list?institute_id=${instituteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBatches(data);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
    }
  };

  const fetchBatchMembers = async (batchId) => {
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/${batchId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBatchMembers(data);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  const fetchAtRiskStudents = async (batchId) => {
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/${batchId}/at-risk`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAtRiskStudents(data);
    } catch (err) {
      console.error('Failed to fetch at-risk students:', err);
    }
  };

  // Phase 4B: Staff Management
  const fetchStaffMembers = async (instituteId) => {
    try {
      const res = await fetch(`${API_URL}/api/institutes/${instituteId}/staff`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStaffMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch staff:', err);
      setStaffMembers([]);
    }
  };

  const addStaffMember = async (e) => {
    e.preventDefault();
    if (!selectedInstitute) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/institutes/${selectedInstitute.id}/staff/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: staffForm.user_id,  // Now sending email instead of user_id
          role: staffForm.role,
          assigned_batches: staffForm.assigned_batches
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add staff');
      }
      
      toast.success('Staff member added!');
      setShowStaffForm(false);
      setStaffForm({ user_id: '', role: 'faculty', assigned_batches: [] });
      fetchStaffMembers(selectedInstitute.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeStaffMember = async (staffId) => {
    if (!selectedInstitute) return;
    try {
      await fetch(`${API_URL}/api/institutes/${selectedInstitute.id}/staff/${staffId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Staff member removed');
      fetchStaffMembers(selectedInstitute.id);
    } catch (err) {
      toast.error('Failed to remove staff');
    }
  };

  // Phase 4B: Academic Schedule
  const fetchScheduleEvents = async (batchId) => {
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/${batchId}/schedule`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setScheduleEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
      setScheduleEvents([]);
    }
  };

  const addScheduleEvent = async (e) => {
    e.preventDefault();
    if (!selectedBatch) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/${selectedBatch.id}/schedule/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          batch_id: selectedBatch.id,
          ...scheduleForm
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add event');
      }
      
      toast.success('Schedule event added!');
      setShowScheduleForm(false);
      setScheduleForm({ title: '', description: '', event_type: 'class', start_date: '', end_date: '', start_time: '', end_time: '' });
      fetchScheduleEvents(selectedBatch.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteScheduleEvent = async (eventId) => {
    try {
      await fetch(`${API_URL}/api/institutes/schedule/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Event deleted');
      if (selectedBatch) fetchScheduleEvents(selectedBatch.id);
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  // Phase 4B: Announcements
  const fetchAnnouncements = async (instituteId) => {
    try {
      const res = await fetch(`${API_URL}/api/institutes/${instituteId}/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
      setAnnouncements([]);
    }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    if (!selectedInstitute) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/institutes/${selectedInstitute.id}/announcements/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          institute_id: selectedInstitute.id,
          batch_id: announcementForm.batch_id || null,
          title: announcementForm.title,
          content: announcementForm.content,
          priority: announcementForm.priority,
          target_roles: announcementForm.target_roles
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create announcement');
      }
      
      toast.success('Announcement posted!');
      setShowAnnouncementForm(false);
      setAnnouncementForm({ title: '', content: '', priority: 'medium', batch_id: '', target_roles: ['student'] });
      fetchAnnouncements(selectedInstitute.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    try {
      await fetch(`${API_URL}/api/institutes/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Announcement deleted');
      if (selectedInstitute) fetchAnnouncements(selectedInstitute.id);
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  // Phase 4C: Operations Functions
  const fetchInterventions = async (instituteId) => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/operations/interventions?institute_id=${instituteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setInterventions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch interventions:', err);
      setInterventions([]);
    }
  };

  const fetchWorkboardStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/operations/workboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setWorkboardStats(data);
    } catch (err) {
      console.error('Failed to fetch workboard stats:', err);
    }
  };

  const fetchWorkboardStudents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/operations/workboard/students?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setWorkboardStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch workboard students:', err);
      setWorkboardStudents([]);
    }
  };

  const createIntervention = async (e) => {
    e.preventDefault();
    if (!selectedInstitute || !selectedBatch) {
      toast.error('Please select an institute and batch first');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/prep-os/operations/interventions/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_email: interventionForm.student_id,  // Now sending email
          intervention_type: interventionForm.intervention_type,
          reason: interventionForm.reason,
          description: interventionForm.description,
          priority: interventionForm.priority,
          batch_id: selectedBatch.id,
          institute_id: selectedInstitute.id
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create intervention');
      }
      
      toast.success('Intervention created!');
      setShowInterventionForm(false);
      setInterventionForm({ student_id: '', intervention_type: 'counseling', reason: '', description: '', priority: 'medium' });
      fetchInterventions(selectedInstitute.id);
      fetchWorkboardStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateInterventionStatus = async (interventionId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/prep-os/operations/interventions/${interventionId}/status?new_status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Status updated to ${newStatus}`);
      if (selectedInstitute) fetchInterventions(selectedInstitute.id);
      fetchWorkboardStats();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Phase 4C: Reports Functions
  const fetchReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/reports/list?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReports([]);
    }
  };

  const generateReport = async (reportType) => {
    setLoading(true);
    try {
      const body = { report_type: reportType };
      if (selectedInstitute) body.institute_id = selectedInstitute.id;
      if (selectedBatch) body.batch_id = selectedBatch.id;
      
      const res = await fetch(`${API_URL}/api/prep-os/reports/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) throw new Error('Failed to generate report');
      
      toast.success('Report generation started!');
      setTimeout(fetchReports, 2000); // Refresh after 2 seconds
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = (reportId) => {
    window.open(`${API_URL}/api/prep-os/reports/${reportId}/download`, '_blank');
  };

  const createInstitute = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/institutes/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(instituteForm)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create institute');
      }
      
      const data = await res.json();
      toast.success(`Institute "${data.name}" created successfully!`);
      setShowInstituteForm(false);
      setInstituteForm({ name: '', code: '', city: '', state: '', contact_email: '' });
      fetchInstitutes();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createBatch = async (e) => {
    e.preventDefault();
    if (!selectedInstitute) {
      toast.error('Please select an institute first');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...batchForm,
          institute_id: selectedInstitute.id
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create batch');
      }
      
      const data = await res.json();
      toast.success(`Batch "${data.name}" created successfully!`);
      setShowBatchForm(false);
      setBatchForm({ name: '', code: '', description: '', target_exam: 'UPSC CSE', target_year: 2026, max_students: 50 });
      fetchBatches(selectedInstitute.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addMemberToBatch = async (e) => {
    e.preventDefault();
    if (!selectedBatch || !memberEmail) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/institutes/batches/members/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: memberEmail,  // Now sending email instead of user_id
          batch_id: selectedBatch.id,
          role: 'student'
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add member');
      }
      
      toast.success('Student added to batch!');
      setShowAddMemberForm(false);
      setMemberEmail('');
      fetchBatchMembers(selectedBatch.id);
    } catch (err) {
      toast.error(err.message || 'Failed to add student. Make sure the email is registered.');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (level) => {
    const colors = {
      RED: 'bg-red-500 text-white',
      YELLOW: 'bg-yellow-500 text-white',
      GREEN: 'bg-green-500 text-white'
    };
    return colors[level] || 'bg-gray-500 text-white';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30" data-testid="prep-os-admin-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                PrepOS Admin
              </h1>
              <p className="text-slate-600">Institute & Batch Management</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
            <TabsList className="bg-white/80 border border-slate-200 mb-6 inline-flex min-w-max">
              <TabsTrigger value="institutes" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-institutes">
                <Building2 className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Institutes</span>
                <span className="sm:hidden">Inst</span>
              </TabsTrigger>
              <TabsTrigger value="batches" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-batches">
                <GraduationCap className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Batches</span>
                <span className="sm:hidden">Batch</span>
              </TabsTrigger>
              <TabsTrigger value="staff" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-staff">
                <UserCog className="h-3 w-3 md:h-4 md:w-4" />
                Staff
              </TabsTrigger>
              <TabsTrigger value="schedule" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-schedule">
                <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Schedule</span>
                <span className="sm:hidden">Sched</span>
              </TabsTrigger>
              <TabsTrigger value="announcements" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-announcements">
                <Megaphone className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Announcements</span>
                <span className="sm:hidden">Ann</span>
              </TabsTrigger>
              <TabsTrigger value="interventions" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-interventions">
                <AlertCircle className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Interventions</span>
                <span className="sm:hidden">Intv</span>
              </TabsTrigger>
              <TabsTrigger value="workboard" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-workboard">
                <Briefcase className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Workboard</span>
                <span className="sm:hidden">Work</span>
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-reports">
                <FileText className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Reports</span>
                <span className="sm:hidden">Rep</span>
              </TabsTrigger>
              <TabsTrigger value="students" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-students">
                <Users className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Students</span>
                <span className="sm:hidden">Stud</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap" data-testid="tab-analytics">
                <BarChart3 className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Analytics</span>
                <span className="sm:hidden">Stats</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Institutes Tab */}
          <TabsContent value="institutes">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Institute List */}
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-indigo-500" />
                      Institutes ({institutes.length})
                    </CardTitle>
                    {/* Only main admin can create new institutes */}
                    {isMainAdmin && (
                      <Button 
                        onClick={() => setShowInstituteForm(true)}
                        className="bg-indigo-500 hover:bg-indigo-600"
                        data-testid="add-institute-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Institute
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {institutes.length === 0 ? (
                      <p className="text-slate-500 text-center py-8" data-testid="no-institutes-msg">No institutes yet. Create one to get started.</p>
                    ) : (
                      <div className="space-y-3" data-testid="institutes-list">
                        {institutes.map(inst => (
                          <div 
                            key={inst.id}
                            onClick={() => {
                              setSelectedInstitute(inst);
                              setActiveTab('batches');
                            }}
                            className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-indigo-300 hover:bg-indigo-50/50 ${
                              selectedInstitute?.id === inst.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-semibold text-slate-800">{inst.name}</h3>
                                <p className="text-sm text-slate-500">{inst.code} • {inst.city}, {inst.state}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-indigo-600">{inst.total_batches} batches</p>
                                <p className="text-xs text-slate-500">{inst.total_students} students</p>
                              </div>
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Create Institute Form */}
              {showInstituteForm && (
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader>
                    <CardTitle>Create Institute</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createInstitute} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Institute Name *</label>
                        <Input
                          value={instituteForm.name}
                          onChange={e => setInstituteForm({...instituteForm, name: e.target.value})}
                          placeholder="e.g., Clarias Academy"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Institute Code *</label>
                        <Input
                          value={instituteForm.code}
                          onChange={e => setInstituteForm({...instituteForm, code: e.target.value.toUpperCase()})}
                          placeholder="e.g., CLARIAS001"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">City</label>
                          <Input
                            value={instituteForm.city}
                            onChange={e => setInstituteForm({...instituteForm, city: e.target.value})}
                            placeholder="Hyderabad"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">State</label>
                          <Input
                            value={instituteForm.state}
                            onChange={e => setInstituteForm({...instituteForm, state: e.target.value})}
                            placeholder="Telangana"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Contact Email</label>
                        <Input
                          type="email"
                          value={instituteForm.contact_email}
                          onChange={e => setInstituteForm({...instituteForm, contact_email: e.target.value})}
                          placeholder="admin@institute.com"
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={loading} className="flex-1 bg-indigo-500 hover:bg-indigo-600">
                          {loading ? 'Creating...' : 'Create Institute'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowInstituteForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Batches Tab */}
          <TabsContent value="batches">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <GraduationCap className="h-5 w-5 text-purple-500" />
                        Batches
                      </CardTitle>
                      {selectedInstitute && (
                        <p className="text-sm text-slate-500 mt-1">
                          {selectedInstitute.name}
                        </p>
                      )}
                    </div>
                    {selectedInstitute && (
                      <Button 
                        onClick={() => setShowBatchForm(true)}
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Batch
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {!selectedInstitute ? (
                      <p className="text-slate-500 text-center py-8">Select an institute first</p>
                    ) : batches.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No batches yet. Create one to get started.</p>
                    ) : (
                      <div className="space-y-3">
                        {batches.map(batch => (
                          <div 
                            key={batch.id}
                            onClick={() => {
                              setSelectedBatch(batch);
                              setActiveTab('students');
                            }}
                            className={`p-4 border rounded-lg cursor-pointer transition-all hover:border-purple-300 hover:bg-purple-50/50 ${
                              selectedBatch?.id === batch.id ? 'border-purple-500 bg-purple-50' : 'border-slate-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-semibold text-slate-800">{batch.name}</h3>
                                <p className="text-sm text-slate-500">{batch.code} • {batch.target_exam} {batch.target_year}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-purple-600">
                                  {batch.current_students}/{batch.max_students} students
                                </p>
                                <Badge variant="outline" className="text-xs">
                                  {batch.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Create Batch Form */}
              {showBatchForm && selectedInstitute && (
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader>
                    <CardTitle>Create Batch</CardTitle>
                    <p className="text-sm text-slate-500">For {selectedInstitute.name}</p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createBatch} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Batch Name *</label>
                        <Input
                          value={batchForm.name}
                          onChange={e => setBatchForm({...batchForm, name: e.target.value})}
                          placeholder="e.g., UPSC 2026 Batch A"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Batch Code *</label>
                        <Input
                          value={batchForm.code}
                          onChange={e => setBatchForm({...batchForm, code: e.target.value.toUpperCase()})}
                          placeholder="e.g., UPSC2026A"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Description</label>
                        <Input
                          value={batchForm.description}
                          onChange={e => setBatchForm({...batchForm, description: e.target.value})}
                          placeholder="Foundation batch for beginners"
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Target Exam</label>
                          <select
                            value={batchForm.target_exam}
                            onChange={e => setBatchForm({...batchForm, target_exam: e.target.value})}
                            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md"
                          >
                            <option value="UPSC CSE">UPSC CSE</option>
                            <option value="TGPSC">TGPSC</option>
                            <option value="APPSC">APPSC</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Target Year</label>
                          <Input
                            type="number"
                            value={batchForm.target_year}
                            onChange={e => setBatchForm({...batchForm, target_year: parseInt(e.target.value)})}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Max Students</label>
                        <Input
                          type="number"
                          value={batchForm.max_students}
                          onChange={e => setBatchForm({...batchForm, max_students: parseInt(e.target.value)})}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={loading} className="flex-1 bg-purple-500 hover:bg-purple-600">
                          {loading ? 'Creating...' : 'Create Batch'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowBatchForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Staff Tab - Phase 4B */}
          <TabsContent value="staff">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <UserCog className="h-5 w-5 text-purple-500" />
                      Staff Members ({staffMembers.length})
                    </CardTitle>
                    <Button 
                      onClick={() => setShowStaffForm(true)}
                      className="bg-purple-500 hover:bg-purple-600"
                      disabled={!selectedInstitute}
                      data-testid="add-staff-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Staff
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!selectedInstitute ? (
                      <p className="text-slate-500 text-center py-8">Select an institute first</p>
                    ) : staffMembers.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No staff members yet</p>
                    ) : (
                      <div className="space-y-3" data-testid="staff-list">
                        {staffMembers.map(staff => (
                          <div 
                            key={staff.id}
                            className="p-4 rounded-lg border border-slate-200 hover:border-purple-300 transition-all flex justify-between items-center"
                          >
                            <div>
                              <p className="font-medium text-slate-900">{staff.user_name || 'Unknown'}</p>
                              <p className="text-sm text-slate-500">{staff.user_email}</p>
                              <Badge className="mt-1 capitalize bg-purple-100 text-purple-700">{staff.role}</Badge>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeStaffMember(staff.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Add Staff Form */}
              {showStaffForm && selectedInstitute && (
                <Card className="bg-white border-purple-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Add Staff Member</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowStaffForm(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={addStaffMember} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Email Address</label>
                        <Input
                          type="email"
                          value={staffForm.user_id}
                          onChange={e => setStaffForm({...staffForm, user_id: e.target.value})}
                          placeholder="user@example.com"
                          required
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Enter the registered email of the user
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Role</label>
                        <Select value={staffForm.role} onValueChange={v => setStaffForm({...staffForm, role: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="director">Director</SelectItem>
                            <SelectItem value="faculty">Faculty</SelectItem>
                            <SelectItem value="mentor">Mentor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="w-full bg-purple-500 hover:bg-purple-600" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Staff'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Roles Info */}
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-purple-600" />
                    Role Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-white/70 rounded-lg">
                    <p className="font-medium text-purple-700">Director</p>
                    <p className="text-xs text-slate-600">Full access: manage staff, batches, announcements</p>
                  </div>
                  <div className="p-3 bg-white/70 rounded-lg">
                    <p className="font-medium text-indigo-700">Faculty</p>
                    <p className="text-xs text-slate-600">View batches, students, reports</p>
                  </div>
                  <div className="p-3 bg-white/70 rounded-lg">
                    <p className="font-medium text-blue-700">Mentor</p>
                    <p className="text-xs text-slate-600">View assigned students, intervene</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Schedule Tab - Phase 4B */}
          <TabsContent value="schedule">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-teal-500" />
                      Academic Schedule {selectedBatch && `- ${selectedBatch.name}`}
                    </CardTitle>
                    <Button 
                      onClick={() => setShowScheduleForm(true)}
                      className="bg-teal-500 hover:bg-teal-600"
                      disabled={!selectedBatch}
                      data-testid="add-schedule-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Event
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!selectedBatch ? (
                      <p className="text-slate-500 text-center py-8">Select a batch to view schedule</p>
                    ) : scheduleEvents.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No schedule events yet</p>
                    ) : (
                      <div className="space-y-3" data-testid="schedule-list">
                        {scheduleEvents.map(event => (
                          <div 
                            key={event.id}
                            className="p-4 rounded-lg border border-slate-200 hover:border-teal-300 transition-all"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-slate-900">{event.title}</p>
                                <p className="text-sm text-slate-500">{event.description}</p>
                                <div className="flex gap-2 mt-2">
                                  <Badge className="capitalize bg-teal-100 text-teal-700">{event.event_type}</Badge>
                                  <span className="text-xs text-slate-500">
                                    {event.start_date} {event.start_time && `at ${event.start_time}`}
                                  </span>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => deleteScheduleEvent(event.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Add Event Form */}
              {showScheduleForm && selectedBatch && (
                <Card className="bg-white border-teal-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Add Schedule Event</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowScheduleForm(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={addScheduleEvent} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Title</label>
                        <Input
                          value={scheduleForm.title}
                          onChange={e => setScheduleForm({...scheduleForm, title: e.target.value})}
                          placeholder="e.g., Weekly Test"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Event Type</label>
                        <Select value={scheduleForm.event_type} onValueChange={v => setScheduleForm({...scheduleForm, event_type: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="class">Class</SelectItem>
                            <SelectItem value="test">Test</SelectItem>
                            <SelectItem value="revision">Revision</SelectItem>
                            <SelectItem value="holiday">Holiday</SelectItem>
                            <SelectItem value="deadline">Deadline</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Start Date</label>
                        <Input
                          type="date"
                          value={scheduleForm.start_date}
                          onChange={e => setScheduleForm({...scheduleForm, start_date: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Start Time (optional)</label>
                        <Input
                          type="time"
                          value={scheduleForm.start_time}
                          onChange={e => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Description</label>
                        <Input
                          value={scheduleForm.description}
                          onChange={e => setScheduleForm({...scheduleForm, description: e.target.value})}
                          placeholder="Optional details"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-600" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Event'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Quick Select Batch */}
              <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
                <CardHeader>
                  <CardTitle className="text-lg">Select Batch</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {batches.map(batch => (
                      <div
                        key={batch.id}
                        onClick={() => setSelectedBatch(batch)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedBatch?.id === batch.id 
                            ? 'bg-teal-500 text-white' 
                            : 'bg-white hover:bg-teal-100'
                        }`}
                      >
                        <p className="font-medium">{batch.name}</p>
                        <p className={`text-xs ${selectedBatch?.id === batch.id ? 'text-teal-100' : 'text-slate-500'}`}>
                          {batch.target_exam} {batch.target_year}
                        </p>
                      </div>
                    ))}
                    {batches.length === 0 && (
                      <p className="text-slate-500 text-sm">No batches - select an institute first</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Announcements Tab - Phase 4B */}
          <TabsContent value="announcements">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Megaphone className="h-5 w-5 text-orange-500" />
                      Announcements ({announcements.length})
                    </CardTitle>
                    <Button 
                      onClick={() => setShowAnnouncementForm(true)}
                      className="bg-orange-500 hover:bg-orange-600"
                      disabled={!selectedInstitute}
                      data-testid="add-announcement-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Announcement
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!selectedInstitute ? (
                      <p className="text-slate-500 text-center py-8">Select an institute first</p>
                    ) : announcements.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No announcements yet</p>
                    ) : (
                      <div className="space-y-3" data-testid="announcements-list">
                        {announcements.map(ann => (
                          <div 
                            key={ann.id}
                            className={`p-4 rounded-lg border-l-4 ${
                              ann.priority === 'urgent' ? 'border-red-500 bg-red-50' :
                              ann.priority === 'high' ? 'border-orange-500 bg-orange-50' :
                              ann.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                              'border-blue-500 bg-blue-50'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">{ann.title}</p>
                                  <Badge className="capitalize text-xs">{ann.priority}</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{ann.content}</p>
                                <p className="text-xs text-slate-400 mt-2">
                                  Posted by {ann.created_by_name} • {new Date(ann.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(ann.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Create Announcement Form */}
              {showAnnouncementForm && selectedInstitute && (
                <Card className="bg-white border-orange-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">New Announcement</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowAnnouncementForm(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createAnnouncement} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Title</label>
                        <Input
                          value={announcementForm.title}
                          onChange={e => setAnnouncementForm({...announcementForm, title: e.target.value})}
                          placeholder="Announcement title"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Content</label>
                        <textarea
                          value={announcementForm.content}
                          onChange={e => setAnnouncementForm({...announcementForm, content: e.target.value})}
                          placeholder="Write your announcement..."
                          className="w-full p-3 border border-slate-200 rounded-lg text-sm resize-none h-24"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Priority</label>
                        <Select value={announcementForm.priority} onValueChange={v => setAnnouncementForm({...announcementForm, priority: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Target Batch (optional)</label>
                        <Select value={announcementForm.batch_id} onValueChange={v => setAnnouncementForm({...announcementForm, batch_id: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="All batches" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">All Batches</SelectItem>
                            {batches.map(batch => (
                              <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={loading}>
                        {loading ? 'Posting...' : 'Post Announcement'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Priority Guide */}
              <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bell className="h-5 w-5 text-orange-600" />
                    Priority Guide
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-sm"><strong>Urgent:</strong> Immediate attention</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-sm"><strong>High:</strong> Important updates</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-sm"><strong>Medium:</strong> General info</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-sm"><strong>Low:</strong> FYI only</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Interventions Tab - Phase 4C */}
          <TabsContent value="interventions">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      Intervention Cases ({interventions.length})
                    </CardTitle>
                    <Button 
                      onClick={() => setShowInterventionForm(true)}
                      className="bg-red-500 hover:bg-red-600"
                      disabled={!selectedInstitute || !selectedBatch}
                      data-testid="create-intervention-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Case
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {!selectedInstitute ? (
                      <p className="text-slate-500 text-center py-8">Select an institute first</p>
                    ) : interventions.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No intervention cases</p>
                    ) : (
                      <div className="space-y-3" data-testid="interventions-list">
                        {interventions.map(intv => (
                          <div 
                            key={intv.id}
                            className={`p-4 rounded-lg border-l-4 ${
                              intv.priority === 'urgent' ? 'border-red-500 bg-red-50' :
                              intv.priority === 'high' ? 'border-orange-500 bg-orange-50' :
                              'border-yellow-500 bg-yellow-50'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">{intv.student_name}</p>
                                  <Badge className="capitalize text-xs">{intv.intervention_type}</Badge>
                                  <Badge variant={intv.status === 'resolved' ? 'default' : 'outline'} className="capitalize text-xs">
                                    {intv.status}
                                  </Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{intv.reason}</p>
                                <p className="text-xs text-slate-400 mt-2">
                                  Batch: {intv.batch_name} • Created {new Date(intv.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Select value={intv.status} onValueChange={(v) => updateInterventionStatus(intv.id, v)}>
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="open">Open</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Create Intervention Form */}
              {showInterventionForm && (
                <Card className="bg-white border-red-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Create Intervention</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowInterventionForm(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createIntervention} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Student Email</label>
                        <Input
                          type="email"
                          value={interventionForm.student_id}
                          onChange={e => setInterventionForm({...interventionForm, student_id: e.target.value})}
                          placeholder="student@example.com"
                          required
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Enter the registered email of the student
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Type</label>
                        <Select value={interventionForm.intervention_type} onValueChange={v => setInterventionForm({...interventionForm, intervention_type: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="counseling">Counseling</SelectItem>
                            <SelectItem value="extra_revision">Extra Revision</SelectItem>
                            <SelectItem value="mentoring">Mentoring</SelectItem>
                            <SelectItem value="parent_meeting">Parent Meeting</SelectItem>
                            <SelectItem value="motivation_boost">Motivation Boost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Priority</label>
                        <Select value={interventionForm.priority} onValueChange={v => setInterventionForm({...interventionForm, priority: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Reason</label>
                        <Input
                          value={interventionForm.reason}
                          onChange={e => setInterventionForm({...interventionForm, reason: e.target.value})}
                          placeholder="e.g., Low consistency"
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full bg-red-500 hover:bg-red-600" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Intervention'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Intervention Guide */}
              <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Intervention Types
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p><strong>Counseling:</strong> One-on-one talk to understand issues</p>
                  <p><strong>Extra Revision:</strong> Additional study sessions</p>
                  <p><strong>Mentoring:</strong> Assign dedicated mentor</p>
                  <p><strong>Parent Meeting:</strong> Involve parents</p>
                  <p><strong>Motivation Boost:</strong> Encouragement sessions</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Workboard Tab - Phase 4C */}
          <TabsContent value="workboard">
            <div className="space-y-6">
              {/* Stats Cards */}
              {workboardStats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="workboard-stats">
                  <Card className="bg-white/80">
                    <CardContent className="p-4 text-center">
                      <Users className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-600">{workboardStats.total_assigned_students}</p>
                      <p className="text-xs text-slate-500">Total Students</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80">
                    <CardContent className="p-4 text-center">
                      <AlertTriangle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-red-600">{workboardStats.at_risk_students}</p>
                      <p className="text-xs text-slate-500">At Risk</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80">
                    <CardContent className="p-4 text-center">
                      <AlertCircle className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-orange-600">{workboardStats.open_interventions}</p>
                      <p className="text-xs text-slate-500">Open Cases</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80">
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-yellow-600">{workboardStats.pending_remedial_actions}</p>
                      <p className="text-xs text-slate-500">Pending Actions</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80">
                    <CardContent className="p-4 text-center">
                      <Activity className="h-6 w-6 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-600">{workboardStats.recent_activity_count}</p>
                      <p className="text-xs text-slate-500">Recent Events</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/80">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-600">{workboardStats.avg_student_consistency}%</p>
                      <p className="text-xs text-slate-500">Avg Consistency</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Student List */}
              <Card className="bg-white/80 border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-indigo-500" />
                    My Students
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchWorkboardStudents}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </CardHeader>
                <CardContent>
                  {workboardStudents.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No assigned students</p>
                  ) : (
                    <div className="space-y-3" data-testid="workboard-students">
                      {workboardStudents.map(student => (
                        <div 
                          key={student.user_id}
                          className="p-4 rounded-lg border border-slate-200 hover:border-indigo-300 transition-all flex justify-between items-center"
                        >
                          <div>
                            <p className="font-medium text-slate-900">{student.name}</p>
                            <p className="text-sm text-slate-500">{student.batch_name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={`text-xs ${
                                student.risk_level === 'RED' ? 'bg-red-100 text-red-700' :
                                student.risk_level === 'YELLOW' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {student.risk_level}
                              </Badge>
                              <span className="text-xs text-slate-500">
                                Consistency: {student.consistency_score}%
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            {student.open_interventions > 0 && (
                              <Badge variant="destructive" className="mb-1">{student.open_interventions} cases</Badge>
                            )}
                            {student.pending_actions > 0 && (
                              <Badge variant="outline" className="ml-1">{student.pending_actions} actions</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reports Tab - Phase 4C */}
          <TabsContent value="reports">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-emerald-500" />
                      Generated Reports ({reports.length})
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchReports}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {reports.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No reports generated yet</p>
                    ) : (
                      <div className="space-y-3" data-testid="reports-list">
                        {reports.map(report => (
                          <div 
                            key={report.id}
                            className="p-4 rounded-lg border border-slate-200 hover:border-emerald-300 transition-all flex justify-between items-center"
                          >
                            <div>
                              <p className="font-medium text-slate-900">{report.title}</p>
                              <p className="text-sm text-slate-500 capitalize">{report.report_type?.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                Generated {new Date(report.generated_at).toLocaleString()}
                              </p>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => downloadReport(report.id)}
                              disabled={report.status !== 'completed'}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              {report.status === 'generating' ? 'Generating...' : 'Download'}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Generate Reports */}
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Generate Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    onClick={() => generateReport('batch_summary')}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    disabled={loading || !selectedBatch}
                  >
                    Batch Summary Report
                  </Button>
                  <Button 
                    onClick={() => generateReport('at_risk_report')}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    disabled={loading}
                  >
                    At-Risk Students Report
                  </Button>
                  <Button 
                    onClick={() => generateReport('institute_overview')}
                    variant="outline"
                    className="w-full"
                    disabled={loading || !selectedInstitute}
                  >
                    Institute Overview
                  </Button>
                  <p className="text-xs text-slate-500 mt-4">
                    Select an institute and/or batch to generate targeted reports.
                    Reports are generated as PDF files.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Student List */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-500" />
                        Batch Students
                      </CardTitle>
                      {selectedBatch && (
                        <p className="text-sm text-slate-500 mt-1">
                          {selectedBatch.name} • {batchMembers.length} students
                        </p>
                      )}
                    </div>
                    {selectedBatch && (
                      <Button 
                        onClick={() => setShowAddMemberForm(true)}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Student
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {!selectedBatch ? (
                      <p className="text-slate-500 text-center py-8">Select a batch first</p>
                    ) : batchMembers.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No students in this batch yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {batchMembers.map(member => (
                          <div 
                            key={member.id}
                            className="p-3 border border-slate-200 rounded-lg flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium text-slate-800">{member.student_name}</p>
                              <p className="text-sm text-slate-500">{member.student_email}</p>
                            </div>
                            <Badge variant="outline">{member.role}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* At-Risk Students */}
                {selectedBatch && atRiskStudents.length > 0 && (
                  <Card className="bg-red-50 border-red-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="h-5 w-5" />
                        At-Risk Students ({atRiskStudents.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {atRiskStudents.map(student => (
                          <div 
                            key={student.student_id}
                            className="p-3 bg-white border border-red-200 rounded-lg"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-slate-800">{student.name}</p>
                              <Badge className={getRiskBadge(student.risk_level)}>
                                {student.risk_level}
                              </Badge>
                            </div>
                            <div className="text-sm text-slate-600">
                              {student.reasons.map((reason, i) => (
                                <p key={i}>• {reason}</p>
                              ))}
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              Consistency: {student.consistency_score}%
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Add Member Form */}
              {showAddMemberForm && selectedBatch && (
                <Card className="bg-white/80 border-slate-200">
                  <CardHeader>
                    <CardTitle>Add Student to Batch</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={addMemberToBatch} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Student Email *</label>
                        <Input
                          type="email"
                          value={memberEmail}
                          onChange={e => setMemberEmail(e.target.value)}
                          placeholder="student@example.com"
                          required
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Enter the registered email of the student
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={loading} className="flex-1 bg-blue-500 hover:bg-blue-600">
                          {loading ? 'Adding...' : 'Add Student'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowAddMemberForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card className="bg-white/80 border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-green-500" />
                  Batch Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedBatch ? (
                  <p className="text-slate-500 text-center py-8">Select a batch to view analytics</p>
                ) : (
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-blue-600">{batchMembers.length}</p>
                      <p className="text-sm text-slate-600">Total Students</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-green-600">
                        {atRiskStudents.filter(s => s.risk_level === 'GREEN').length || batchMembers.length - atRiskStudents.length}
                      </p>
                      <p className="text-sm text-slate-600">On Track</p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-yellow-600">
                        {atRiskStudents.filter(s => s.risk_level === 'YELLOW').length}
                      </p>
                      <p className="text-sm text-slate-600">Needs Attention</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-3xl font-bold text-red-600">
                        {atRiskStudents.filter(s => s.risk_level === 'RED').length}
                      </p>
                      <p className="text-sm text-slate-600">At Risk</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
